import { useCallback, useEffect, useMemo, useState } from "react";
import { describeWaitlistReason } from "../waitlistReason";
import { api } from "../api";
import type { WaitlistStatus, WaitlistSubmission } from "../types";
import { PromoteDialog } from "./PromoteDialog";

const TABS: { key: WaitlistStatus; label: string }[] = [
  { key: "new", label: "Needs review" },
  { key: "approved_pending_activation", label: "Pending activation" },
  { key: "activation_expired", label: "Expired" },
  { key: "approved", label: "Active" },
  { key: "rejected", label: "Rejected" },
];

const STATUS_LABELS: Record<WaitlistStatus, string> = {
  new: "Needs review",
  approved_pending_activation: "Approved · Pending activation",
  activation_expired: "Activation expired",
  approved: "Active",
  rejected: "Rejected",
};

const PROMOTABLE = new Set<WaitlistStatus>([
  "new",
  "approved_pending_activation",
  "activation_expired",
]);

function fmtDate(v: string | null | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

function describe(s: WaitlistSubmission): string {
  const role = s.role === "other" ? s.roleOther || "other" : s.role;
  const goal = s.goal === "other" ? s.goalOther || "other" : s.goal;
  return [role, goal].filter(Boolean).join(" · ") || "—";
}

function statusBadge(status: WaitlistStatus): string {
  if (status === "approved") return "badge ok";
  if (status === "rejected") return "badge bad";
  if (status === "approved_pending_activation") return "badge live";
  if (status === "activation_expired") return "badge warn";
  return "badge";
}

function approvalResult(s: WaitlistSubmission): string {
  if (s.status === "rejected") return "Rejected manually";
  if (s.status === "approved") {
    return s.approvedBy === "website_auto_access"
      ? "Approved automatically"
      : "Approved manually";
  }
  if (
    s.status === "approved_pending_activation" ||
    s.status === "activation_expired"
  ) {
    return "Approved automatically";
  }
  return s.accessEvaluation?.outcome === "waitlist"
    ? "Sent to manual review"
    : "Evaluation pending";
}

interface WaitlistProps {
  initialSubmissionId?: string;
}

export function Waitlist({ initialSubmissionId = "" }: WaitlistProps) {
  const [rows, setRows] = useState<WaitlistSubmission[]>([]);
  const [tab, setTab] = useState<WaitlistStatus>("new");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [promoting, setPromoting] = useState<WaitlistSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const page = await api.listWaitlist();
      let nextRows = page.items;
      let selected = initialSubmissionId
        ? nextRows.find((row) => row.id === initialSubmissionId)
        : undefined;

      // Notification links stay useful after the submission falls outside the
      // newest-100 list window: fetch that exact row and prepend it locally.
      if (initialSubmissionId && !selected) {
        selected = await api.getWaitlist(initialSubmissionId);
        nextRows = [selected, ...nextRows];
      }

      setRows(nextRows);
      if (selected) {
        setTab(selected.status);
        setExpanded(selected.id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [initialSubmissionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(() => {
    const c: Record<WaitlistStatus, number> = {
      new: 0,
      approved_pending_activation: 0,
      activation_expired: 0,
      approved: 0,
      rejected: 0,
    };
    for (const r of rows) c[r.status] += 1;
    return c;
  }, [rows]);

  const visible = useMemo(
    () => rows.filter((r) => r.status === tab),
    [rows, tab],
  );

  async function reject(id: string) {
    if (!confirm("Reject this submission?")) return;
    try {
      await api.reject(id);
      setRows((rs) =>
        rs.map((r) => (r.id === id ? { ...r, status: "rejected" } : r)),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function markApproved(id: string) {
    setRows((rs) =>
      rs.map((r) =>
        r.id === id
          ? {
              ...r,
              status: "approved",
              accessActive: true,
              accessReserved: false,
            }
          : r,
      ),
    );
    setTab("approved");
    setExpanded(id);
    void load();
  }

  return (
    <section>
      <header className="section-head">
        <h2>Waitlist</h2>
        <button className="ghost" onClick={() => void load()} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </header>

      <div className="tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? "tab active" : "tab"}
            onClick={() => setTab(t.key)}
          >
            {t.label} <span className="count">{counts[t.key]}</span>
          </button>
        ))}
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {!loading && visible.length === 0 && (
        <p className="muted empty">Nothing here.</p>
      )}

      {visible.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Status</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Device</th>
                <th>Role · Goal</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => {
                const reason = describeWaitlistReason(s);
                const android = (s.mobileOs ?? "").toLowerCase() === "android";
                const open = expanded === s.id;
                const promotable = PROMOTABLE.has(s.status);
                const highlighted = initialSubmissionId === s.id;
                return [
                  <tr key={s.id} className={highlighted ? "row-highlight" : undefined}>
                    <td className="nowrap">{fmtDate(s.submittedAt)}</td>
                    <td className="nowrap">
                      <span className={statusBadge(s.status)}>
                        {STATUS_LABELS[s.status]}
                      </span>
                      {s.status === "new" && s.accessEvaluation?.reasonCode && <div style={{whiteSpace: "normal", maxWidth: 220, marginTop: 6}}>{reason.summary}</div>}
                    </td>
                    <td>{s.email || "—"}</td>
                    <td>
                      <code>{s.phone || "—"}</code>
                    </td>
                    <td>
                      {android ? (
                        <span className="badge warn" title="iMessage only">
                          android
                        </span>
                      ) : (
                        <span className="badge">{s.mobileOs || "—"}</span>
                      )}
                    </td>
                    <td>{describe(s)}</td>
                    <td className="right nowrap">
                      <button
                        className="ghost small"
                        onClick={() => setExpanded(open ? null : s.id)}
                      >
                        {open ? "Less" : "More"}
                      </button>
                      {promotable && (
                        <button
                          className="primary small"
                          disabled={android}
                          title={
                            android
                              ? "Android signups cannot be added to the iMessage allowlist"
                              : s.status === "approved_pending_activation"
                                ? "Grant permanent access now instead of waiting for first-message activation"
                                : undefined
                          }
                          onClick={() => setPromoting(s)}
                        >
                          Promote
                        </button>
                      )}
                      {s.status === "new" && (
                        <button
                          className="ghost small"
                          onClick={() => void reject(s.id)}
                        >
                          Reject
                        </button>
                      )}
                    </td>
                  </tr>,
                  open && (
                    <tr key={`${s.id}-detail`} className="detail">
                      <td colSpan={7}>
                        <dl className="kv">
                          <dt>Approval result</dt>
                          <dd>{approvalResult(s)}</dd>
                          {s.accessEvaluation?.reasonCode && (
                            <>
                              <dt>原始审批原因</dt>
                              <dd>
                                <strong>{reason.summary}</strong>
                                {reason.detail && <div>{reason.detail}</div>}
                                {reason.evidence && <div className="muted">{reason.evidence}</div>}
                                {reason.next && <div>建议处理：{reason.next}</div>}
                                {s.status !== "new" && s.accessEvaluation.outcome === "waitlist" && <div>以上为提交时的判断；当前处理状态以本条申请状态为准。</div>}
                              </dd>
                            </>
                          )}
                          {s.activationExpiresAt && (
                            <>
                              <dt>Activation deadline</dt>
                              <dd>{fmtDate(s.activationExpiresAt)}</dd>
                            </>
                          )}
                          {s.goalDetails && (
                            <>
                              <dt>Details</dt>
                              <dd>{s.goalDetails}</dd>
                            </>
                          )}
                          <dt>SMS consent</dt>
                          <dd>{s.smsConsent || "—"}</dd>
                          <dt>Source</dt>
                          <dd>
                            {s.source || "—"} · {s.page || "—"}
                          </dd>
                          {s.status === "approved" && (
                            <>
                              <dt>Approved by</dt>
                              <dd>
                                {s.approvedBy || "—"} · {fmtDate(s.approvedAt)}
                              </dd>
                              <dt>Activated at</dt>
                              <dd>{fmtDate(s.accessActivatedAt)}</dd>
                              <dt>Allowlisted as</dt>
                              <dd>
                                <code>
                                  {(s.allowlistIds ?? []).join(", ") ||
                                    s.accessHandle ||
                                    "—"}
                                </code>
                              </dd>
                            </>
                          )}
                          {s.status === "rejected" && (
                            <>
                              <dt>Rejected by</dt>
                              <dd>
                                {s.rejectedBy || "—"} · {fmtDate(s.rejectedAt)}
                              </dd>
                            </>
                          )}
                        </dl>
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}

      {promoting && (
        <PromoteDialog
          submission={promoting}
          onClose={() => setPromoting(null)}
          onApproved={markApproved}
        />
      )}
    </section>
  );
}
